import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ChevronLeft, Target, MapPin, Calendar, Users, TrendingUp, IndianRupee,
  Stethoscope, User, Briefcase,
} from 'lucide-react';
import useApiMethods from '../../lib/useApiMethods';
import { cn } from '../../utils/tailwindUtils';


const STATUS_BADGE = {
  planned: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  live: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  completed: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};

const formatINR = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const fmt = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';


export default function HealthCampDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetch: apiFetch } = useApiMethods();
  const [camp, setCamp] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cResp, eResp] = await Promise.all([
          apiFetch(`engage/health-camps/${id}/`),
          apiFetch('engage/enquiries/'),
        ]);
        if (cancelled) return;
        setCamp(cResp?.data || cResp?.data?.data || null);
        const all = eResp?.results || eResp?.data?.results || [];
        setEnquiries(all.filter(e => e.source === 'health_camp' && String(e.source_details?.health_camp_id) === String(id)));
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || 'Failed to load camp');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading && !camp) return <div className="p-8 text-center text-gray-500 dark:text-gray-400">Loading…</div>;
  if (!camp) return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300">{error || 'Camp not found.'}</div>
      <button onClick={() => navigate('/engage/health-camps')} className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm"><ChevronLeft className="w-3.5 h-3.5" /> Back</button>
    </div>
  );

  const attended = camp.actual_footfall || 0;
  const converted = enquiries.filter(e => e.status === 'converted').length;
  const spend = Number(camp.actual_spend || camp.budget || 0);
  const cpa = converted > 0 ? spend / converted : 0;
  const attendanceRate = camp.expected_footfall ? Math.round((attended / camp.expected_footfall) * 100) : null;
  const convRate = enquiries.length ? Math.round((converted / enquiries.length) * 100) : 0;

  return (
    <div className="p-4 max-w-[1400px] mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <button onClick={() => navigate('/engage/health-camps')} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
          <ChevronLeft className="w-4 h-4" /> Back to Health Camps
        </button>
        <button onClick={() => navigate(`/engage/health-camps/${id}/edit`)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm hover:bg-gray-50 dark:hover:bg-gray-700">Edit</button>
      </div>

      <div className="bg-gradient-to-r from-pink-500/5 to-rose-500/5 dark:from-pink-500/10 dark:to-rose-500/10 rounded-xl p-4 border border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center text-white shadow-sm">
            <Target className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">{camp.name}</h1>
            <div className="flex items-center gap-2 mt-0.5 text-sm text-gray-600 dark:text-gray-400">
              <span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium', STATUS_BADGE[camp.status] || STATUS_BADGE.planned)}>{camp.status}</span>
              {camp.specialty_focus && <span className="inline-flex items-center gap-1"><Stethoscope className="w-3 h-3" /> {camp.specialty_focus}</span>}
            </div>
          </div>
        </div>
      </div>

      {error && <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">{error}</div>}

      {/* ROI KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-2"><Users className="w-4 h-4 text-blue-500" /><span className="text-[10px] uppercase text-gray-500 font-medium">Attended</span></div>
          <div className="text-xl font-bold mt-1 text-gray-900 dark:text-white">
            {attended}
            {camp.expected_footfall && <span className="text-xs text-gray-500 ml-1">/ {camp.expected_footfall} exp.</span>}
          </div>
          {attendanceRate !== null && <div className="text-[10px] text-gray-500 mt-0.5">{attendanceRate}% of expected</div>}
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-2"><TrendingUp className="w-4 h-4 text-purple-500" /><span className="text-[10px] uppercase text-gray-500 font-medium">Leads gen.</span></div>
          <div className="text-xl font-bold mt-1 text-gray-900 dark:text-white">{enquiries.length}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-2"><TrendingUp className="w-4 h-4 text-green-500" /><span className="text-[10px] uppercase text-gray-500 font-medium">Converted</span></div>
          <div className="text-xl font-bold mt-1 text-green-600 dark:text-green-400">
            {converted}<span className="text-xs text-gray-500 ml-1">({convRate}%)</span>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-2"><IndianRupee className="w-4 h-4 text-amber-500" /><span className="text-[10px] uppercase text-gray-500 font-medium">Cost / patient</span></div>
          <div className="text-xl font-bold mt-1 text-amber-600 dark:text-amber-400">₹ {formatINR(cpa)}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1 bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 space-y-1.5 text-sm">
          <h3 className="text-xs font-semibold text-gray-900 dark:text-white mb-2">Camp details</h3>
          {camp.start_datetime && <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5 text-gray-400" />{fmt(camp.start_datetime)}</div>}
          {camp.location_name && <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-gray-400" />{camp.location_name}</div>}
          {camp.organising_manager_name && <div className="flex items-center gap-2"><User className="w-3.5 h-3.5 text-gray-400" />Manager: {camp.organising_manager_name}</div>}
          {camp.partner_corporate_name && <div className="flex items-center gap-2"><Briefcase className="w-3.5 h-3.5 text-gray-400" />Partner: {camp.partner_corporate_name}</div>}
          <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
            <IndianRupee className="w-3.5 h-3.5 text-gray-400" />
            <span>Budget: ₹ {formatINR(camp.budget)}</span>
          </div>
          <div className="flex items-center gap-2">
            <IndianRupee className="w-3.5 h-3.5 text-gray-400" />
            <span>Spend: ₹ {formatINR(camp.actual_spend)}</span>
          </div>
          {camp.description && <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700"><p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{camp.description}</p></div>}
        </div>

        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700"><h3 className="text-sm font-semibold text-gray-900 dark:text-white">Attendees who became enquiries ({enquiries.length})</h3></div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Name</th>
                <th className="px-4 py-2 text-left font-medium">Interest</th>
                <th className="px-4 py-2 text-left font-medium">Stage</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {enquiries.length === 0 ? (
                <tr><td colSpan={3} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">No enquiries from this camp yet.</td></tr>
              ) : enquiries.map(e => (
                <tr key={e.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                  <td className="px-4 py-2.5"><Link to={`/engage/enquiries/${e.id}`} className="font-medium text-blue-600 dark:text-blue-400 hover:underline">{e.name}</Link></td>
                  <td className="px-4 py-2.5 text-[12px] text-gray-700 dark:text-gray-300">{e.department_of_interest || '—'}</td>
                  <td className="px-4 py-2.5 text-[12px] text-gray-700 dark:text-gray-300">{e.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
